<template>
  <v-row class="theme_surface_background pl-2">
    <v-col cols="12" class="pt-3 pb-0">
      <p class="mt-2">{{ $t('views.incidentForm.addDetails') }}</p>
    </v-col>
    <v-col cols="12">
      <p class="mt-2 mb-1 orange_colored">
        {{ $t('common.concepts.overview') }}
      </p>
      <v-text-field
        v-model="incidentVModel.name"
        variant="outlined"
        :label="$t('domain.fields.incidentName')"
        density="compact"
        hide-details
        color="rgb(var(--v-theme-lighten))"></v-text-field>
    </v-col>

    <v-col cols="12">
      <ZDateInput
        v-model="dateField"
        :timezone="timezone"
        :format="tenantDateFormat"
        hide-details
        variant="outlined"
        density="compact"></ZDateInput>
    </v-col>
    <v-col cols="12">
      <ZTimeInput v-model="timeField" :timezone="timezone" hide-details variant="outlined" density="compact"></ZTimeInput>
    </v-col>

    <v-col cols="12">
      <v-select
        v-model="incidentVModel.category"
        variant="outlined"
        dense
        :label="$t('domain.fields.category')"
        :items="categoryValues"
        item-value="id"
        item-title="i18NKey"
        density="compact"
        return-object
        hide-details>
        <template #item="{ item, props }">
          <v-list-item v-bind="props" :title="$t(`domain.enums.incidentCategory.${item.title}`)" />
        </template>
        <template #selection="{ item }">
          {{ $t(`domain.enums.incidentCategory.${item.title}`) }}
        </template>
      </v-select>
    </v-col>

    <v-col cols="12">
      <v-select
        v-model="incidentVModel.complexity"
        variant="outlined"
        dense
        :label="$t('domain.fields.complexity')"
        density="compact"
        :items="complexityValues"
        item-value="id"
        item-title="name"
        return-object
        hide-details>
        <template #item="{ item, props }">
          <v-list-item v-bind="props" :title="$t(`domain.enums.complexity.${item.title.toLowerCase()}`)" />
        </template>
        <template #selection="{ item }">
          {{ $t(`domain.enums.complexity.${item.title.toLowerCase()}`) }}
        </template>
      </v-select>
    </v-col>

    <v-col cols="12">
      <v-select
        v-model="incidentVModel.displayMode"
        variant="outlined"
        dense
        :label="$t('domain.fields.displayMode')"
        density="compact"
        :items="displayModeValues"
        item-title="name"
        item-value="id"
        return-object
        hide-details>
        <template #item="{ item, props }">
          <v-list-item v-bind="props" :title="$t(`domain.enums.displayMode.${item.title.toLowerCase()}`)" />
        </template>
        <template #selection="{ item }">
          {{ $t(`domain.enums.displayMode.${item.title.toLowerCase()}`) }}
        </template>
      </v-select>
    </v-col>

    <v-col cols="12" class="pb-0">
      <p class="mt-2 mb-1 orange_colored">
        {{ $t('domain.fields.measurements') }}
      </p>
    </v-col>
    <v-col cols="6 pt-0">
      <v-text-field
        v-model.number="sizeMeassurement"
        type="number"
        variant="outlined"
        :label="$t('domain.units.hectares')"
        density="compact"
        hide-details
        color="rgb(var(--v-theme-lighten))"></v-text-field>
    </v-col>

    <v-col cols="6 pt-0">
      <v-text-field
        v-model.number="containmentMeassurement"
        type="number"
        variant="outlined"
        :label="$t('domain.fields.containment')"
        density="compact"
        hide-details
        color="rgb(var(--v-theme-lighten))"></v-text-field>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
// Framework import
import type { Ref, PropType } from 'vue'
import { computed } from 'vue'

import { storeToRefs } from 'pinia'

import { asyncComputed, useVModel } from '@vueuse/core'

import { utcVal } from '@tsyl/radon'

// Services import
import getMeassurementData from '@/composables/getMeassurementData'
import { getCategories, getComplexity, getDisplayModes } from '@/services/api/masterData'
import SettingsStore from '@/stores/settingsStore'

//Types import
import type { Incident } from '@/types/Incident'

// Components import

const props = defineProps({
  incident: {
    type: Object as PropType<Partial<Incident>>,
    default: () => ({})
  }
})

// undefined keeps the component's own default; only a tenant with a fixed format overrides it
const { tenantDateFormat } = storeToRefs(SettingsStore())

const timezone = computed(() => {
  return import.meta.env.VITE_TIMEZONE
})

const emits = defineEmits<{
  (event: 'update:incident', v: Partial<Incident>): void
}>()

const incidentVModel = useVModel(props, 'incident', emits) as Ref<Incident>

const dateField = computed({
  get() {
    return utcVal(incidentVModel.value.declared_timestamp) ?? ''
  },
  set(value: string) {
    incidentVModel.value.declared_timestamp = value
  }
})

const timeField = computed({
  get() {
    return utcVal(incidentVModel.value.declared_timestamp) ?? ''
  },
  set(value: string) {
    incidentVModel.value.declared_timestamp = value
  }
})

const sizeMeassurement = getMeassurementData(incidentVModel, 'Size')
const containmentMeassurement = getMeassurementData(incidentVModel, 'Containment')

const categoryValues = asyncComputed(async () => {
  return await getCategories()
})
const complexityValues = asyncComputed(async () => {
  return await getComplexity()
})
const displayModeValues = asyncComputed(async () => {
  return await getDisplayModes()
})
</script>

<style scoped></style>
